import {
    Avatar,
    Box,
    Breadcrumbs,
    Button,
    Container,
    Divider,
    Grid,
    IconButton,
    Link,
    Modal,
    TextField,
    Tooltip,
    Typography,
    useTheme,
    capitalize,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../helpers/AuthProvider";
import { Close } from "@mui/icons-material";
import { Formik, ErrorMessage, Form } from "formik";
import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
import MiniBot from "../../MiniBot";
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer";
import queryGraphql from "../../../helpers/ApiInterface"; // Added API Helper

// --- Interfaces ---
// Updated interface to match component needs. Data will be mapped from API response.
interface GatewayData {
    id: string;
    name: string;
    apiType: string;
    endpointType: string;
    userPoolName?: string; // Made optional as API doesn't provide it
    region: string;
    wafEnabled: boolean;
    tokenHeaderSource: string;
    status: string;
    solutionOwner: string;
    createdAt: string;
    invokeUrl?: string; // Made optional, will be fetched separately
    reasonForRejection?: string;
}

// --- Hardcoded Data Removed ---

const rejectValidationSchema = Yup.object().shape({
    reasonForRejection: Yup.string().required("Reason for rejection is required").min(5, "Reason must be at least 5 characters long"),
});

// --- Helper Component ---
const DetailItem: React.FC<{ label: string; value?: string | number | boolean | null }> = ({ label, value }) => {
    const displayValue = value ?? '-';
    return (
        <Box mb={2}>
            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
            <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>
                {typeof displayValue === 'boolean' ? (displayValue ? 'Enabled' : 'Disabled') : displayValue}
            </Typography>
        </Box>
    );
};

const APIGatewayApproveFormPageContent: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const [gateway, setGateway] = useState<GatewayData | null>(null);
    const [invokeUrl, setInvokeUrl] = useState<string| null>(null);
    const [apiGatewayRepo, setApiGatewayRepo] = useState<string| null>(null);
    const [openRejectModal, setOpenRejectModal] = useState(false);
    const [openApproveModal, setOpenApproveModal] = useState(false);


    const isViewer = user.roles?.some((role: string) => role === "viewer") || false;
    // Effect to fetch the main gateway data
    useEffect(() => {
        if (!id) return;

        const fetchGatewayData = async () => {
            try {
                const query = `
                    query GetAPIGateway($getApiGatewayId: String!) {
                      getAPIGateway(id: $getApiGatewayId) {
                        id
                        name
                        apiGatewayType
                        apiEndpointType
                        region
                        tokenHeaderSource
                        awsWafEnabled
                        status
                        reasonForRejection
                        userPoolName
                        createdAt
                        apiGatewayOwner {
                          id
                          name
                        }
                      }
                    }
                `;
                const response = await queryGraphql(query, { getApiGatewayId: id });
                const apiData = response.getAPIGateway;

                if (apiData) {
                    // Transform API data to the shape our component expects (GatewayData interface)
                    const transformedGateway: GatewayData = {
                        id: apiData.id,
                        name: apiData.name,
                        apiType: apiData.apiGatewayType,
                        endpointType: apiData.apiEndpointType,
                        region: apiData.region,
                        wafEnabled: apiData.awsWafEnabled,
                        tokenHeaderSource: apiData.tokenHeaderSource,
                        status: apiData.status.toLowerCase(),
                        solutionOwner: apiData.apiGatewayOwner?.name || 'N/A',
                        createdAt: new Date(Number(apiData.createdAt)).toISOString(),
                        reasonForRejection: apiData.reasonForRejection,
                        userPoolName : apiData.userPoolName,
                        // userPoolName and invokeUrl are not in this query
                    };
                    setGateway(transformedGateway);
                }
            } catch (error) {
                console.error("Error fetching gateway details:", error);
                setGateway(null);
            }
        };

        fetchGatewayData();
    }, [id]);

    // Secondary effect to fetch the Invoke URL ONLY IF the gateway becomes active
    useEffect(() => {
        const fetchInvokeUrl = async () => {
            // Only run if we have a gateway, its status is 'active', and it doesn't already have an invokeUrl
            if (!gateway || gateway.status !== 'active' || !gateway.id || gateway.invokeUrl) {
                return;
            }
            try {
                // Fetch the URL from the resourceStatus endpoint
                const data = await queryGraphql(
                    `query Query($resourceStatusId: String!) {
                      resourceStatus(id: $resourceStatusId) {
                          solutionEndpointUrl
                          projectUrl
                      }
                    }`, 
                    { resourceStatusId: gateway.id }
                );
                if (data.resourceStatus?.solutionEndpointUrl && data.resourceStatus?.projectUrl) {
                    // Update the gateway state with the new URL
                    // setGateway(prevGateway => ({
                    //     ...prevGateway!,
                    //     invokeUrl: data.resourceStatus.solutionEndpointUrl
                    // }));
                    setInvokeUrl(data.resourceStatus.solutionEndpointUrl);
                    setApiGatewayRepo(data.resourceStatus.projectUrl);
                }
            } catch (error) {
                console.error("Error fetching invoke URL:", error);
            }
        };

        fetchInvokeUrl();
    }, [gateway]); // Runs whenever the 'gateway' object changes

    const handleApprove = async () => {
        if (!id) return;
        try {
            const response = await queryGraphql(
                `mutation ApproveAPIGateway($input: ApproveAPIGatewayInput!) {
                  approveAPIGateway(input: $input) { id }
                }`,
                { input: { id } }
            );

            if (response.approveAPIGateway?.id) {
                setOpenApproveModal(false);
                navigate("/apis-gateway/requests");
            }
        } catch (error) {
            console.error("Error approving gateway:", error);
            // Optionally: show an error alert to the user
        }
    };
    
    // handleReject is now replaced by the Formik onSubmit logic directly

    const statusDisplay = gateway?.status ? capitalize(gateway.status) : "Status Unknown";
    const statusBgColor = gateway?.status === "active" ? "#12B76A10" : gateway?.status === "rejected" ? "#F0443810" : "#FDB02210";
    const statusIcon = gateway?.status === "active" ? "/assets/tickcircle.svg" : gateway?.status === "rejected" ? "/assets/reject.svg" : "/assets/clock.svg";

    if (!gateway) return <Container sx={{p: 4, textAlign: 'center'}}>Loading gateway details...</Container>;

    return (
        <Container disableGutters maxWidth={false} sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
            <MiniBot />
            <Box sx={{ p: 2, bgcolor: "#FFFFFF" }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
                    <Link underline="hover" color="inherit" href="/apis-gateway/requests" onClick={(e) => { e.preventDefault(); navigate("/apis-gateway/requests") }}> API Gateway Requests </Link>
                    <Typography color="text.primary">Gateway Details</Typography>
                </Breadcrumbs>
                <Box display="flex" alignItems="center" mt={1}>
                    <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/apis-gateway/requests")}>
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Typography fontFamily="Gilroy-Bold" fontSize="18px">Gateway Details</Typography>
                </Box>
            </Box>
            <Grid container justifyContent="center">
                <Grid item xs={11}> 
                    <Box my={2}>
                        <Box bgcolor={statusBgColor} borderRadius="12px 12px 0px 0px" p={2} boxShadow={1}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                <Box>
                                    <Typography variant="h5" fontFamily="Gilroy-Bold" gutterBottom>{gateway.name}</Typography>
                                    <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{`Resource Owner: ${gateway.solutionOwner}`}</Typography>
                                    {/* This UI logic remains valid, as reasonForRejection is in the API response */}
                                    {gateway.status === 'rejected' && gateway.reasonForRejection && (
                                        <Chip label={`Rejection Reason: ${gateway.reasonForRejection}`} size="small" sx={{mt: 1, height: 'auto', '& .MuiChip-label': { display: 'block', whiteSpace: 'normal' }}} />
                                    )}
                                </Box>
                                <Box display="flex" alignItems="center">
                                    <img src={statusIcon} alt="Status Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold" sx={{textTransform: 'capitalize'}}>{statusDisplay}</Typography>
                                </Box>
                            </Box>
                        </Box>
                        
                        <Box bgcolor="#fff" p={3} borderRadius="0px 0px 12px 12px" boxShadow={1}>
                            <Grid container spacing={2.5}>
                                <Grid item xs={12} sm={6} md={3}><DetailItem label="API Type" value={gateway.apiType} /></Grid>
                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Endpoint Type" value={gateway.endpointType} /></Grid>
                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Region" value={gateway.region?.toUpperCase()} /></Grid>
                                {/* userPoolName is not in the query, so DetailItem will correctly render '-' */}
                                <Grid item xs={12} sm={6} md={3}><DetailItem label="User Pool" value={gateway.userPoolName} /></Grid>
                            </Grid>

                            <Divider sx={{ my: 2.5 }} />
                            <Typography variant="subtitle1" fontFamily="Gilroy-SemiBold" sx={{ mb: 2 }}>Gateway Configuration</Typography>
                            <Grid container spacing={2.5}>
                                <Grid item xs={12} sm={6} md={3}><DetailItem label="WAF" value={gateway.wafEnabled} /></Grid>
                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Token Header Source" value={gateway.tokenHeaderSource} /></Grid>
                                {/* This conditional block remains valid. It will render once the second useEffect fetches and sets the invokeUrl. */}
                                {invokeUrl && (
                                                            <Grid item xs={6} sm={3}>
                                                                <Typography 
                                                                    variant="caption" 
                                                                    display="block" 
                                                                    sx={{ fontFamily: 'Gilroy-Regular', fontSize: '12px', color: '#666666' }}
                                                                >
                                                                    API Gateway Endpoint
                                                                </Typography>
                                                                <Tooltip
                                                                    title={
                                                                        <>
                                                                            <Typography 
                                                                                fontFamily="Gilroy-Bold" 
                                                                                fontSize="14px"
                                                                            >
                                                                                API Gateway Endpoint
                                                                            </Typography>
                                                                            
                                                                            {/* Conditionally render Link vs. plain text */}
                                                                            {invokeUrl && invokeUrl !== "Loading..." ? (
                                                                                <Link
                                                                                    href={invokeUrl}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    sx={{
                                                                                        color: '#ffffff',
                                                                                        textDecoration: 'underline',
                                                                                        wordBreak: 'break-all' // Ensures long URLs wrap
                                                                                    }}
                                                                                >
                                                                                    Gateway URL
                                                                                </Link>
                                                                            ) : (
                                                                                <Typography 
                                                                                    fontFamily="Gilroy-Regular" 
                                                                                    fontSize="12px" 
                                                                                    sx={{ color: '#ffffff' }}
                                                                                >
                                                                                    Loading...
                                                                                </Typography>
                                                                            )}
                                                                        </>
                                                                    }
                                                                    slotProps={{ tooltip: { sx: { bgcolor: '#333333' } } }}
                                                                >
                                                                    <IconButton sx={{ p: 0 }}>
                                                                        <Avatar
                                                                            src="/assets/info.svg"
                                                                            alt="info"
                                                                            sx={{ height: 16, width: 16, cursor: 'pointer' }}
                                                                        />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Grid>
                                                        )}
                                
                                                        {apiGatewayRepo && (
                                                            <Grid item xs={6} sm={3}>
                                                                <Typography 
                                                                    variant="caption" 
                                                                    display="block" 
                                                                    sx={{ fontFamily: 'Gilroy-Regular', fontSize: '12px', color: '#666666' }}
                                                                >
                                                                    API Gateway Repo
                                                                </Typography>
                                                                <Tooltip
                                                                    title={
                                                                        <>
                                                                            <Typography 
                                                                                fontFamily="Gilroy-Bold" 
                                                                                fontSize="14px"
                                                                            >
                                                                                API Gateway Repo
                                                                            </Typography>
                                                                            
                                                                            {/* Conditionally render Link vs. plain text */}
                                                                            {apiGatewayRepo && apiGatewayRepo !== "Loading..." ? (
                                                                                <Link
                                                                                    href={apiGatewayRepo}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    sx={{
                                                                                        color: '#ffffff',
                                                                                        textDecoration: 'underline',
                                                                                        wordBreak: 'break-all' // Ensures long URLs wrap
                                                                                    }}
                                                                                >
                                                                                    Gateway Repo
                                                                                </Link>
                                                                            ) : (
                                                                                <Typography 
                                                                                    fontFamily="Gilroy-Regular" 
                                                                                    fontSize="12px" 
                                                                                    sx={{ color: '#ffffff' }}
                                                                                >
                                                                                    Loading...
                                                                                </Typography>
                                                                            )}
                                                                        </>
                                                                    }
                                                                    slotProps={{ tooltip: { sx: { bgcolor: '#333333' } } }}
                                                                >
                                                                    <IconButton sx={{ p: 0 }}>
                                                                        <Avatar
                                                                            src="/assets/info.svg"
                                                                            alt="info"
                                                                            sx={{ height: 16, width: 16, cursor: 'pointer' }}
                                                                        />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Grid>
                                                        )}   
                                
                            </Grid>
                        </Box>
                    </Box>
                    
                    {gateway?.status === "pending" && (
                        <Box display="flex" justifyContent="flex-end" gap={2} my={3}>
                            <Button sx={{ color: "black",borderColor: "black",fontWeight: "bold",textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px" }} variant="outlined" size="large" onClick={() => setOpenRejectModal(true)} disabled={isViewer}>Reject Request</Button>
                            <Button sx={{ backgroundColor: "black",color: "white",fontWeight: "bold","&:hover": {backgroundColor: "white",color: "black",borderColor: "black",},textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px" }} variant="contained" size="large" disabled={isViewer} onClick={() => setOpenApproveModal(true)}>Approve Request</Button>
                        </Box>
                    )}

                    <Modal open={openApproveModal} onClose={() => setOpenApproveModal(false)}>
    <Box sx={{ 
        position: "absolute", 
        top: "50%", 
        left: "50%", 
        transform: "translate(-50%, -50%)", 
        width: { xs: '90%', md: 500 }, 
        bgcolor: "white", 
        boxShadow: 24, 
        borderRadius: "16px" 
    }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
            <Typography fontFamily="Gilroy-SemiBold" fontSize="20px">
                Approve Request
            </Typography>
            <IconButton onClick={() => setOpenApproveModal(false)}>
                <Close fontSize="small" />
            </IconButton>
        </Box>
        <Divider />
        <Box p={3}>
            <Typography>
                Are you sure you want to approve this API Gateway request?
            </Typography>
            <Box 
    sx={{ 
        mt: 2, 
        p: 2, 
        bgcolor: 'grey.100',
        borderRadius: '8px', 
        border: '1px solid',
        borderColor: 'grey.200'
    }}
>
    <Typography 
        fontSize="14px" 
        fontFamily="Gilroy-SemiBold" 
        sx={{ mb: 1 }}
    >
        This action will generate the following credentials:
    </Typography>
    {/* Use MUI List for proper alignment and spacing */}
    <List sx={{ p: 0 }} dense>
        <ListItem sx={{ p: 0 }}>
            <ListItemIcon sx={{ minWidth: '20px' }}>
                <FiberManualRecordIcon sx={{ fontSize: '8px' }} />
            </ListItemIcon>
            <ListItemText 
                primary="Client ID" 
                primaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '14px' }} 
            />
        </ListItem>
        <ListItem sx={{ p: 0 }}>
            <ListItemIcon sx={{ minWidth: '20px' }}>
                <FiberManualRecordIcon sx={{ fontSize: '8px' }} />
            </ListItemIcon>
            <ListItemText 
                primary="Client Secret" 
                primaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '14px' }} 
            />
        </ListItem>
    </List>
</Box>
        </Box>
        <Divider />
        <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
            <Button 
                variant="outlined" 
                onClick={() => setOpenApproveModal(false)} 
                sx={{ color: 'black', borderColor: 'black', textTransform: 'none' }}
            >
                Cancel
            </Button>
            <Button 
                onClick={handleApprove} // This now calls the API-enabled function
                sx={{ 
                    backgroundColor: "black", 
                    color: "white", 
                    textTransform: "none", 
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" } 
                }} 
                variant="contained"
            >
                Approve
            </Button>
        </Box>
    </Box>
</Modal>

                    <Modal open={openRejectModal} onClose={() => setOpenRejectModal(false)}>
                        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: '90%', md: 700 }, bgcolor: "white", boxShadow: 24, borderRadius: "16px" }}>
                            <Formik
                                initialValues={{ reasonForRejection: "" }}
                                validationSchema={rejectValidationSchema}
                                onSubmit={async ({ reasonForRejection }) => {
                                    // Replaced handleReject call with direct API logic
                                    if (!id) return;
                                    try {
                                        const response = await queryGraphql(
                                            `mutation RejectAPIGateway($input: RejectAPIGatewayInput!) {
                                              rejectAPIGateway(input: $input) { id }
                                            }`,
                                            { input: { id, reasonForRejection } }
                                        );
                                        if (response.rejectAPIGateway?.id) {
                                            setOpenRejectModal(false);
                                            navigate("/apis-gateway/requests");
                                        }
                                    } catch (error) {
                                        console.error("Error rejecting gateway:", error);
                                        // Optionally show an error to the user
                                    }
                                }}
                            >
                                {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
                                    <Form id="reject-gateway-form">
                                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}><Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Reject API Gateway Request</Typography><IconButton onClick={() => setOpenRejectModal(false)}><Close fontSize="small" /></IconButton></Box>
                                        <Divider />
                                        <Box sx={{ p: 3 }}>
                                            <CompulsoryFieldsLabel fieldName="Comments" />
                                            <TextField variant="filled" fullWidth multiline rows={4} name="reasonForRejection" value={values.reasonForRejection} onChange={handleChange} onBlur={handleBlur} placeholder="Enter comments for rejection..." hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} error={touched.reasonForRejection && Boolean(errors.reasonForRejection)} />
                                            <Box sx={{ color: "red", mt: 1 }}><ErrorMessage name="reasonForRejection" /></Box>
                                        </Box>
                                        <Divider />
                                        <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
                                            <Button variant="outlined" onClick={() => setOpenRejectModal(false)} sx={{ color: 'black', borderColor: 'black', textTransform: 'none' }}>Cancel</Button>
                                            <Button type="submit" form="reject-gateway-form" disabled={!isValid || !dirty} sx={{ backgroundColor: "black", color: "white", textTransform: "none", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" }, '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)' } }} variant="contained">Reject</Button>
                                        </Box>
                                    </Form>
                                )}
                            </Formik>
                        </Box>
                    </Modal>
                </Grid>
            </Grid>
        </Container>
    );
};

const APIGatewayApproveFormPage: React.FC = () => {
    const theme = useTheme();
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(() => typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('isFaradayDrawerOpen') || 'true') : true);
    const handleDrawerStateChange = (isOpen: boolean) => {
        setIsDrawerOpen(isOpen);
        if (typeof window !== 'undefined') localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpen));
    };
    const currentMarginLeft = isDrawerOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"requests-apis-gateway"} onStateChange={handleDrawerStateChange} />
            <Box component="main" sx={{ flexGrow: 1, bgcolor: '#fafafa', p: 0, transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen }), marginLeft: { xs: 0, sm: `${currentMarginLeft}px` } }}>
                <APIGatewayApproveFormPageContent />
            </Box>
        </Box>
    );
};

export default APIGatewayApproveFormPage;